/**
 * Page timeline (`/origins/:siteUuid/pages/:pageUuid`) — per-page
 * Core Web Vitals timeline + recent audits panel.
 *
 * Replacement for legacy `PageTimelineIsland.tsx`. Features per
 * `05-pages.md` §5b:
 *   - Pill row (region / range / device — device today, range today;
 *     region is Gap: the API accepts `region` but there's no regions
 *     catalogue endpoint in the dashboard-v2 hook surface yet so the
 *     toggle is omitted until Stage 6 wires `/v1/management/regions`).
 *   - Stat grid of p75 CWV metrics (LCP / CLS / INP / TTFB / FCP).
 *   - Per-metric Sparkline built from samples (plan doc says Recharts,
 *     we ship a self-contained SVG sparkline — Recharts isn't in
 *     package.json; flagged in handover).
 *   - Recent audits panel — page-scoped `AuditsTable` variant.
 *   - Empty state with "Monitor this page" + RunIt fallback CTAs.
 */
import { AuditsTable } from '@/components/primitives/AuditsTable';
import { EmptyState } from '@/components/primitives/EmptyState';
import { ErrorState } from '@/components/primitives/ErrorState';
import { PageActions } from '@/components/primitives/PageActions';
import { Sparkline } from '@/components/primitives/Sparkline';
import { Island } from '@/components/shell/Island';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { P75Row, SamplesRow } from '@/lib/api-types';
import {
  clsColor,
  formatMs,
  hostOf,
  inpColor,
  lcpColor,
  relativeTime,
  scoreColor,
} from '@/lib/format';
import { useAudits, usePage, usePageP75, usePageSamples, useSite } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { ArrowSquareOut, ChartLine, Desktop, DeviceMobile, Pulse } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useMemo, useState } from 'react';

export const Route = createFileRoute('/_app/origins/$siteUuid/pages/$pageUuid')({
  component: PageTimelinePage,
});

type DeviceFilter = 'mobile' | 'desktop' | 'all';
type RangeDays = 7 | 30 | 90;

function PageTimelinePage(): ReactElement {
  const { siteUuid, pageUuid } = Route.useParams();

  const [device, setDevice] = useState<DeviceFilter>('all');
  const [rangeDays, setRangeDays] = useState<RangeDays>(30);

  const site = useSite(siteUuid);
  const page = usePage(pageUuid);

  const baseFilter = {
    days: rangeDays,
    ...(device !== 'all' ? { device_type: device } : {}),
  };

  const p75 = usePageP75(pageUuid, baseFilter);
  const samples = usePageSamples(pageUuid, baseFilter);

  // Fetch all audits for the workspace, then filter client-side to
  // this page. Gap: the server's `GET /v1/audits` doesn't accept a
  // pageUuid filter today. This is cheap for small workspaces and
  // react-query caches the full list separately anyway.
  const audits = useAudits({ limit: 50, offset: 0 });
  const pageAudits = useMemo(() => {
    if (!audits.data) return [];
    return audits.data.audits.filter((a) => a.ids?.page_uuid === pageUuid).slice(0, 20);
  }, [audits.data, pageUuid]);

  const breadcrumb = [
    { label: 'Origins', href: '/origins' },
    {
      label: site.data ? hostOf(site.data.url) : siteUuid,
      href: `/origins/${siteUuid}`,
    },
    { label: site.data?.name ?? siteUuid, href: `/origins/${siteUuid}` },
    { label: page.data?.name ?? pageUuid },
  ];

  const p75Row = p75.data?.[0];

  return (
    <Island
      breadcrumb={breadcrumb}
      actions={
        <PageActions>
          {page.data && (
            <Button
              variant="outline"
              // biome-ignore lint/a11y/useAnchorContent: content lives in the Button children which Base UI merges into the rendered <a>
              render={<a href={page.data.url} target="_blank" rel="noreferrer noopener" />}
            >
              <ArrowSquareOut aria-hidden="true" />
              Visit
            </Button>
          )}
          <Button render={<Link to="/monitors/new" search={{ pageUuid, siteUuid }} />}>
            <Pulse aria-hidden="true" />
            Monitor this page
          </Button>
        </PageActions>
      }
    >
      {page.isError && !page.data ? (
        <ErrorState
          title="Couldn't load this page"
          error={page.error}
          retry={() => {
            void page.refetch();
          }}
          extra={
            <Button variant="ghost" render={<Link to="/origins/$siteUuid" params={{ siteUuid }} />}>
              Back to site
            </Button>
          }
        />
      ) : (
        <div className="space-y-6">
          {/* Filter pill row */}
          <div className="flex flex-wrap items-center gap-3">
            <SegmentedControl<DeviceFilter>
              value={device}
              onChange={setDevice}
              options={[
                { value: 'all', label: 'All' },
                { value: 'mobile', label: 'Mobile', icon: DeviceMobile },
                { value: 'desktop', label: 'Desktop', icon: Desktop },
              ]}
              ariaLabel="Device filter"
            />
            <SegmentedControl<RangeDays>
              value={rangeDays}
              onChange={setRangeDays}
              options={[
                { value: 7, label: '7d' },
                { value: 30, label: '30d' },
                { value: 90, label: '90d' },
              ]}
              ariaLabel="Date range"
            />
          </div>

          {/* Stat grid */}
          <StatGrid row={p75Row} loading={p75.isLoading} />

          {/* Charts */}
          <div className="grid gap-4 lg:grid-cols-2">
            <MetricChart
              label="LCP (ms)"
              samples={samples.data}
              loading={samples.isLoading}
              pick={(r) => r.lcp}
              unit="ms"
            />
            <MetricChart
              label="CLS"
              samples={samples.data}
              loading={samples.isLoading}
              pick={(r) => r.cls}
            />
            <MetricChart
              label="INP (ms)"
              samples={samples.data}
              loading={samples.isLoading}
              pick={(r) => r.inp}
              unit="ms"
            />
            <MetricChart
              label="Performance score"
              samples={samples.data}
              loading={samples.isLoading}
              pick={(r) => r.performance_score}
            />
          </div>

          {/* Recent audits */}
          <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
            <header className="flex items-center justify-between border-b border-[var(--color-line-soft)] px-4 py-3">
              <div>
                <h3 className="text-sm font-semibold text-[var(--color-ink)]">Recent audits</h3>
                <p className="text-xs text-[var(--color-mute)]">Latest runs against this page.</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                render={<Link to="/settings/logs" search={{ pageUuid }} />}
              >
                Open full audit log →
              </Button>
            </header>
            <div className="p-2">
              {audits.isLoading && (
                <div className="space-y-2 p-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              )}
              {!audits.isLoading && pageAudits.length === 0 && (
                <EmptyState
                  icon={ChartLine}
                  title="No audits yet for this page"
                  description="Add a monitor to start populating the timeline, or run a one-off audit."
                  action={
                    <Button render={<Link to="/monitors/new" search={{ pageUuid, siteUuid }} />}>
                      <Pulse aria-hidden="true" />
                      Monitor this page
                    </Button>
                  }
                />
              )}
              {!audits.isLoading && pageAudits.length > 0 && (
                <AuditsTable audits={pageAudits} variant="page-scoped" />
              )}
            </div>
          </section>
        </div>
      )}
    </Island>
  );
}

// -----------------------------------------------------------------
// Stat grid
// -----------------------------------------------------------------

function StatGrid({
  row,
  loading,
}: {
  row: P75Row | undefined;
  loading: boolean;
}): ReactElement {
  if (loading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length skeleton grid
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }
  if (!row || row.sample_count === 0) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--color-line)] bg-[var(--color-paper-alt)]/30 p-6 text-center text-sm text-[var(--color-mute)]">
        No samples yet in this window. Widen the date range or run a fresh audit.
      </div>
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <StatTile label="p75 LCP" value={formatMs(row.p75_lcp)} tone={lcpColor(row.p75_lcp)} />
      <StatTile
        label="p75 CLS"
        value={row.p75_cls == null ? '—' : row.p75_cls.toFixed(2)}
        tone={clsColor(row.p75_cls)}
      />
      <StatTile label="p75 INP" value={formatMs(row.p75_inp)} tone={inpColor(row.p75_inp)} />
      <StatTile label="p75 TTFB" value={formatMs(row.p75_ttfb)} />
      <StatTile
        label="Performance"
        value={
          row.p75_performance_score == null ? '—' : String(Math.round(row.p75_performance_score))
        }
        tone={scoreColor(row.p75_performance_score)}
      />
    </div>
  );
}

function StatTile({
  label,
  value,
  tone = 'text-[var(--color-ink)]',
}: {
  label: string;
  value: string;
  tone?: string;
}): ReactElement {
  return (
    <div className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
        {label}
      </div>
      <div className={cn('mt-1 text-2xl font-semibold leading-none', tone)}>{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------
// Chart card
// -----------------------------------------------------------------

function MetricChart({
  label,
  samples,
  loading,
  pick,
  unit,
}: {
  label: string;
  samples: SamplesRow[] | undefined;
  loading: boolean;
  pick: (row: SamplesRow) => number | null;
  unit?: string;
}): ReactElement {
  const data = useMemo(() => {
    if (!samples) return [];
    // Chronological order for the sparkline (server returns desc by
    // timestamp, we want left-to-right time).
    return samples
      .map((s) => ({
        x: new Date(s.timestamp).getTime(),
        y: pick(s),
      }))
      .sort((a, b) => a.x - b.x);
  }, [samples, pick]);

  const latest = data.at(-1)?.y ?? null;
  const latestFormatted =
    latest == null
      ? '—'
      : unit === 'ms'
        ? formatMs(latest)
        : latest < 10
          ? latest.toFixed(2)
          : String(Math.round(latest));
  const lastTimestamp = data.at(-1)?.x ?? null;

  return (
    <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <header className="mb-3 flex items-center justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
            {label}
          </div>
          <div className="mt-1 text-2xl font-semibold leading-none text-[var(--color-ink)]">
            {latestFormatted}
          </div>
        </div>
        {lastTimestamp && (
          <span className="text-xs text-[var(--color-mute)]">
            {relativeTime(new Date(lastTimestamp).toISOString())}
          </span>
        )}
      </header>
      {loading ? (
        <Skeleton className="h-16 w-full" />
      ) : (
        <Sparkline data={data} width={480} height={72} ariaLabel={label} />
      )}
    </section>
  );
}

// -----------------------------------------------------------------
// Segmented control — plain buttons + a shared style. The coss
// ToggleGroup (base-ui) API is multi-select-centric and was fighting
// the single-value filter UX; inline pill-switch keeps the code
// direct.
// -----------------------------------------------------------------

function SegmentedControl<V extends string | number>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: V;
  onChange: (next: V) => void;
  options: Array<{ value: V; label: string; icon?: typeof DeviceMobile }>;
  ariaLabel: string;
}): ReactElement {
  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: segmented control intentionally uses div+role="group"; <fieldset> would add a default border and legend we don't want
      role="group"
      aria-label={ariaLabel}
      className="inline-flex items-center overflow-hidden rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] text-sm"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        const Icon = opt.icon;
        return (
          <button
            key={String(opt.value)}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 outline-none transition-colors',
              active
                ? 'bg-[var(--color-paper-alt)] font-medium text-[var(--color-ink)]'
                : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-paper-alt)]/60',
            )}
          >
            {Icon && <Icon aria-hidden="true" size={14} />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
